import { useMemo, useState, type FormEvent } from 'react';
import {
  useAddAgEntries,
  useAddAgEntry,
  useAgRequest,
  useCreateAgRequest,
  useDeleteAgEntry,
  useUpdateAgEntry,
  useWorldOptions,
} from '../api/queries';
import type { AgBulkResult, AgEntry, User } from '../api/types';
import { Spinner } from '../components/Spinner';
import { useToast } from '../components/Toast';

const COORDINATES_PATTERN = /^(\d{1,4})\s*[|,]\s*(\d{1,4})$/;

function parseCoordinates(value: string): { x: number; y: number } | null {
  const match = COORDINATES_PATTERN.exec(value.trim());
  return match ? { x: Number(match[1]), y: Number(match[2]) } : null;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * AG-Abfrage: Mitglieder geben an, in welchem Dorf (Koordinate) wie viele AGs
 * stehen -- ändern, löschen und neue ergänzen. Ein Stammesführer/Administrator
 * startet eine neue Anfrage (setzt alle Angaben zurück, kündigt es per Discord
 * an) und sieht/ändert/löscht die Angaben aller Mitglieder der Welt.
 */
export function AgRequestPage({ user }: { user: User }) {
  const needsWorldChoice = user.world === null;
  const [worldKey, setWorldKey] = useState('');
  const worlds = useWorldOptions('login');
  const activeWorld = user.world?.key ?? worldKey;
  const overview = useAgRequest(activeWorld || undefined);
  const data = overview.data;

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h1>AG-Abfrage</h1>
          <p className="muted">
            Gib an, in welchen deiner Dörfer wie viele AGs bereitstehen. Die Angaben nutzen die Stammesführer für die
            AG-Planung.
          </p>
        </div>
      </div>

      {needsWorldChoice && (
        <label className="field">
          <span>Welt</span>
          <select value={worldKey} onChange={(event) => setWorldKey(event.target.value)} required>
            <option value="" disabled>
              Welt wählen …
            </option>
            {(worlds.data?.worlds ?? []).map((world) => (
              <option key={world.key} value={world.key}>
                {world.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {needsWorldChoice && !worldKey && <p className="muted center pad">Wähle eine Welt, um ihre AG-Abfrage zu sehen.</p>}

      {activeWorld && overview.isPending && <Spinner label="AG-Abfrage wird geladen …" />}
      {activeWorld && overview.isError && <div className="flash flash-error">{overview.error.message}</div>}

      {activeWorld && data && (
        <>
          <RequestBar worldKey={activeWorld} data={data} />
          {data.request && <OwnEntries worldKey={activeWorld} entries={data.mine} />}
          {data.canManage && data.request && <AllEntries worldKey={activeWorld} entries={data.all ?? []} />}
        </>
      )}
    </section>
  );
}

function RequestBar({ worldKey, data }: { worldKey: string; data: NonNullable<ReturnType<typeof useAgRequest>['data']> }) {
  const create = useCreateAgRequest(worldKey);
  const toast = useToast();

  const handleCreate = () => {
    const warning = data.request
      ? 'Neue AG-Anfrage erstellen? Alle bisherigen AG-Angaben der Mitglieder werden gelöscht, und die Mitglieder werden per Discord (falls eingerichtet) aufgefordert, ihre AGs neu anzugeben.'
      : 'Erste AG-Anfrage erstellen? Die Mitglieder werden per Discord (falls eingerichtet) aufgefordert, ihre AGs anzugeben.';
    if (!window.confirm(warning)) return;
    create.mutate(undefined, {
      onSuccess: () => toast.push('success', 'Neue AG-Anfrage wurde erstellt.'),
      onError: (error) => toast.push('error', error.message),
    });
  };

  return (
    <div className="ag-request-bar">
      {data.request ? (
        <p className="muted">
          Aktuelle AG-Anfrage vom <strong>{formatDate(data.request.createdAt)}</strong>
          {data.request.createdByName ? ` von ${data.request.createdByName}` : ''}.
        </p>
      ) : (
        <p className="muted">
          Es gibt noch keine AG-Anfrage.
          {data.canManage ? ' Erstelle eine, damit die Mitglieder ihre AGs angeben können.' : ' Ein Stammesführer muss zuerst eine erstellen.'}
        </p>
      )}
      {data.canManage && (
        <button type="button" className="btn btn-primary btn-sm" onClick={handleCreate} disabled={create.isPending}>
          {create.isPending ? 'Wird erstellt …' : data.request ? 'Neue AG-Anfrage erstellen' : 'AG-Anfrage erstellen'}
        </button>
      )}
    </div>
  );
}

function OwnEntries({ worldKey, entries }: { worldKey: string; entries: AgEntry[] }) {
  const add = useAddAgEntry(worldKey);
  const addBulk = useAddAgEntries(worldKey);
  const toast = useToast();
  const [coordinates, setCoordinates] = useState('');
  const [count, setCount] = useState('1');
  const [bulkText, setBulkText] = useState('');
  const [bulkResult, setBulkResult] = useState<AgBulkResult | null>(null);

  const parsed = parseCoordinates(coordinates);
  const countNumber = Number(count);
  const valid = parsed !== null && Number.isInteger(countNumber) && countNumber >= 1;
  const total = entries.reduce((sum, e) => sum + e.count, 0);

  const handleAdd = (event: FormEvent) => {
    event.preventDefault();
    if (!valid || !parsed) return;
    add.mutate(
      { x: parsed.x, y: parsed.y, count: countNumber },
      {
        onSuccess: () => {
          toast.push('success', `${countNumber} AG(s) für ${parsed.x}|${parsed.y} gespeichert.`);
          setCoordinates('');
          setCount('1');
        },
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  const handleBulk = () => {
    addBulk.mutate(
      { text: bulkText },
      {
        onSuccess: (result) => {
          setBulkResult(result);
          if (result.saved > 0) toast.push('success', `${result.saved} Dorf/Dörfer gespeichert.`);
          if (result.rejected.length === 0) setBulkText('');
        },
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  return (
    <>
      <h2>Meine AGs</h2>
      <form className="form" onSubmit={handleAdd} noValidate>
        <div className="field-row">
          <label className="field">
            <span>Koordinate</span>
            <input type="text" value={coordinates} onChange={(e) => setCoordinates(e.target.value)} placeholder="511|432" required />
          </label>
          <label className="field">
            <span>Anzahl AGs</span>
            <input type="number" min={1} value={count} onChange={(e) => setCount(e.target.value)} required />
          </label>
          <div className="field" style={{ justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={!valid || add.isPending}>
              {add.isPending ? 'Wird gespeichert …' : 'Hinzufügen'}
            </button>
          </div>
        </div>
        <span className="hint">Nur Dörfer deiner eigenen Spielfigur. Steht das Dorf schon in der Liste, wird die Anzahl überschrieben.</span>
      </form>

      <details>
        <summary>Mehrere Dörfer auf einmal eintragen</summary>
        <label className="field" style={{ marginTop: 8 }}>
          <span>Eine Zeile je Dorf: Koordinate, Leerzeichen, Anzahl</span>
          <textarea rows={5} value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder={'511|432 2\n512|433 1'} />
        </label>
        <div className="button-row">
          <button type="button" className="btn btn-sm" disabled={!bulkText.trim() || addBulk.isPending} onClick={handleBulk}>
            {addBulk.isPending ? 'Wird gespeichert …' : 'Alle speichern'}
          </button>
        </div>
        {bulkResult && bulkResult.rejected.length > 0 && (
          <ul className="muted" style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            {bulkResult.rejected.map((r, i) => (
              <li key={i}>
                <code>{r.line}</code> – {r.reason}
              </li>
            ))}
          </ul>
        )}
      </details>

      {entries.length === 0 ? (
        <p className="muted center pad">Du hast noch keine AGs angegeben.</p>
      ) : (
        <>
          <p className="muted">
            Gesamt: <strong>{total}</strong> AG{total === 1 ? '' : 's'} in {entries.length} {entries.length === 1 ? 'Dorf' : 'Dörfern'}.
          </p>
          <EntryTable worldKey={worldKey} entries={entries} showUser={false} />
        </>
      )}
    </>
  );
}

function AllEntries({ worldKey, entries }: { worldKey: string; entries: AgEntry[] }) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return entries;
    return entries.filter((e) => e.userName.toLowerCase().includes(query) || (e.villageName ?? '').toLowerCase().includes(query) || `${e.x}|${e.y}`.includes(query));
  }, [entries, search]);
  const total = entries.reduce((sum, e) => sum + e.count, 0);
  const members = new Set(entries.map((e) => e.userId)).size;

  return (
    <>
      <h2>Alle Mitglieder</h2>
      <p className="muted">
        <strong>{total}</strong> AGs in {entries.length} Dörfern von {members} Mitglied{members === 1 ? '' : 'ern'}. Als Stammesführer/Administrator
        kannst du alle Angaben ändern und löschen.
      </p>
      <label className="field">
        <span>Suchen (Spieler, Dorf, Koordinate)</span>
        <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
      </label>
      {filtered.length === 0 ? (
        <p className="muted center pad">{entries.length === 0 ? 'Noch keine Angaben eingegangen.' : 'Keine Treffer.'}</p>
      ) : (
        <EntryTable worldKey={worldKey} entries={filtered} showUser />
      )}
    </>
  );
}

function EntryTable({ worldKey, entries, showUser }: { worldKey: string; entries: AgEntry[]; showUser: boolean }) {
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            {showUser && <th>Spieler</th>}
            <th>Dorf</th>
            <th>Koordinate</th>
            <th>Anzahl AGs</th>
            <th className="right">Aktion</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <EntryRow key={entry.id} worldKey={worldKey} entry={entry} showUser={showUser} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EntryRow({ worldKey, entry, showUser }: { worldKey: string; entry: AgEntry; showUser: boolean }) {
  const update = useUpdateAgEntry(worldKey);
  const remove = useDeleteAgEntry(worldKey);
  const toast = useToast();
  const [count, setCount] = useState(String(entry.count));
  const dirty = Number(count) !== entry.count;
  const valid = Number.isInteger(Number(count)) && Number(count) >= 1;

  return (
    <tr>
      {showUser && <td>{entry.userName}</td>}
      <td>{entry.villageName ?? <span className="muted">unbekannt</span>}</td>
      <td className="nowrap">
        {entry.x}|{entry.y}
      </td>
      <td>
        <input
          type="number"
          min={1}
          value={count}
          onChange={(e) => setCount(e.target.value)}
          style={{ width: 80 }}
          aria-label={`Anzahl AGs in ${entry.x}|${entry.y}`}
        />
      </td>
      <td className="right">
        <div className="actions">
          <button
            type="button"
            className="btn btn-sm"
            disabled={!dirty || !valid || update.isPending}
            onClick={() =>
              update.mutate(
                { id: entry.id, count: Number(count) },
                { onSuccess: () => toast.push('success', 'Anzahl gespeichert.'), onError: (error) => toast.push('error', error.message) },
              )
            }
          >
            Speichern
          </button>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            disabled={remove.isPending}
            onClick={() => {
              if (!window.confirm(`Eintrag ${entry.x}|${entry.y} löschen?`)) return;
              remove.mutate(
                { id: entry.id },
                { onSuccess: () => toast.push('success', 'Eintrag gelöscht.'), onError: (error) => toast.push('error', error.message) },
              );
            }}
          >
            Löschen
          </button>
        </div>
      </td>
    </tr>
  );
}
